import { t } from '../../../shared/config/translations'
import { footerTemplate } from '../../components/layoutTemplates'
import recordPlayerTemplate from '../../components/recordPlayerTemplates'

type TourSlide = {
	key: string
	visualHtml: () => string
}

const DISCOGS_SIGN_UP_URL = 'https://www.discogs.com/users/create'

// Scene content is fictional sample data; scenes are decorative and hidden from assistive technology.
const TOUR_SLIDES: TourSlide[] = [
	{ key: 'welcome', visualHtml: recordPlayerTemplate },
	{ key: 'shuffle', visualHtml: shuffleSceneTemplate },
	{ key: 'browse', visualHtml: browseSceneTemplate },
	{ key: 'history', visualHtml: historySceneTemplate },
	{ key: 'fresh', visualHtml: freshSceneTemplate },
	{ key: 'friends', visualHtml: friendsSceneTemplate },
]

export function loginPageTemplate(): string {
	const total = TOUR_SLIDES.length

	return `
        <div id="login-page">
            <header class="login-header">
                <img class="login-logo" src="/assets/svg/claim.svg" alt="Tunetoss logo" />
            </header>
            <section class="login-tour" aria-roledescription="carousel" aria-label="${t('onboarding.carouselLabel')}">
                <div class="login-tour-viewport">
                    <button class="login-tour-arrow is-prev btn icon noFrame" type="button" data-tour-prev aria-label="${t('onboarding.previous')}" disabled>
                        <svg aria-hidden="true"><use href="#symbols-arrow-back"></use></svg>
                    </button>
                    <div class="login-tour-track" data-tour-track tabindex="0" aria-live="polite">
                        ${TOUR_SLIDES.map((slide, index) => tourSlideTemplate(slide, index, total)).join('')}
                    </div>
                    <button class="login-tour-arrow is-next btn icon noFrame" type="button" data-tour-next aria-label="${t('onboarding.next')}">
                        <svg aria-hidden="true"><use href="#symbols-arrow-forward"></use></svg>
                    </button>
                </div>
                <div class="login-tour-dots" role="tablist" aria-label="${t('onboarding.dotsLabel')}">
                    ${TOUR_SLIDES.map(
						(slide, index) => `
                            <button
                                class="login-tour-dot"
                                type="button"
                                role="tab"
                                data-tour-dot="${index}"
                                aria-controls="login-slide-${slide.key}"
                                aria-selected="${index === 0}"
                                aria-label="${t(`onboarding.${slide.key}.title`)}"
                                tabindex="${index === 0 ? 0 : -1}"
                            ></button>
                        `,
					).join('')}
                </div>
                <p class="login-tour-notice f small secondary">${t('onboarding.previewNotice')}</p>
            </section>
            <div class="login-actions">
                <button class="button-82-pushable" data-discogs-login type="button">
                    <span class="button-82-shadow"></span>
                    <span class="button-82-edge"></span>
                    <span class="button-82-front text">
                        ${t('auth.loginButton')}
                    </span>
                </button>
                <p class="login-sign-up f small secondary">
                    ${t('onboarding.newToDiscogs')}
                    <a href="${DISCOGS_SIGN_UP_URL}" target="_blank" rel="noopener noreferrer">${t('onboarding.createDiscogsAccount')}</a>
                </p>
            </div>
            <div class="login-footer">
                ${footerTemplate({
					secondaryHtml: `
                        <p class="login-discogs-notice f small secondary">
                            This application uses Discogs’ API but is not affiliated with, sponsored or endorsed by Discogs.
                            “Discogs” is a trademark of Zink Media, LLC.
                        </p>
                        <nav class="login-footer-links" aria-label="Information">
                            <a href="/privacy" data-public-route>Privacy Policy</a>
                            <a href="/support" data-public-route>Support</a>
                        </nav>
                    `,
				})}
            </div>
        </div>
    `
}

function tourSlideTemplate(
	slide: TourSlide,
	index: number,
	total: number,
): string {
	return `
        <article
            id="login-slide-${slide.key}"
            class="login-slide${index === 0 ? ' is-active' : ''}"
            data-tour-slide="${index}"
            role="group"
            aria-roledescription="slide"
            aria-label="${t('onboarding.slideLabel')
				.replace('%{index}', String(index + 1))
				.replace('%{total}', String(total))}"
        >
            <div class="login-slide-visual login-scene-${slide.key}" aria-hidden="true">
                ${slide.visualHtml()}
            </div>
            <div class="login-slide-text">
                <h2>${t(`onboarding.${slide.key}.title`)}</h2>
                <p class="f secondary">${t(`onboarding.${slide.key}.body`)}</p>
            </div>
        </article>
    `
}

const SEARCH_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"></circle><path d="m20 20-3.5-3.5"></path></svg>`
const FUNNEL_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z"></path></svg>`
const STAR_ICON = `<svg viewBox="0 0 24 24" fill="currentColor"><path d="m12 2.5 2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.4l-5.9 3.1 1.2-6.5-4.8-4.6 6.6-.9z"></path></svg>`

function coverTemplate(variant: number): string {
	return `<span class="scene-cover cover-${variant}"></span>`
}

/** Shows `before` first, then rolls to `after`, which is also the static final frame. */
function swapTemplate(before: string, after: string): string {
	return `<span class="scene-swap"><span>${before}</span><span>${after}</span></span>`
}

function starsTemplate(count: number): string {
	return `<span class="scene-stars">${STAR_ICON.repeat(count)}</span>`
}

function shuffleSceneTemplate(): string {
	const filters = [
		{ label: 'Format', value: 'Any format' },
		{ label: 'Rating', value: 'Any rating' },
		{ label: 'Genre', value: swapTemplate('Any genre', 'Jazz') },
		{ label: 'Style', value: 'Any style' },
		{ label: 'From', value: swapTemplate('Any year', '1970') },
		{ label: 'Until', value: 'Any year' },
	]

	return `
        <div class="scene-frame">
            <div class="scene-screen scene-shuffle">
                <strong class="scene-heading">Find your next spin</strong>
                <span class="scene-items-pill">
                    <svg><use href="#symbols-collection"></use></svg>
                    ${swapTemplate('120', '34')} items
                </span>
                <div class="scene-filter-grid">
                    ${filters
						.map(
							(filter) => `
                                <span class="scene-filter">
                                    <span class="scene-filter-text">
                                        <span class="scene-filter-label">${filter.label}</span>
                                        <span class="scene-filter-value">${filter.value}</span>
                                    </span>
                                    <svg><use href="#symbols-arrow-down"></use></svg>
                                </span>
                            `,
						)
						.join('')}
                </div>
                <div class="scene-filter-grid">
                    <span class="scene-filter is-switch">
                        <span class="scene-filter-text">
                            <svg class="is-heart"><use href="#symbols-heart"></use></svg>
                            <span>Favorites only</span>
                        </span>
                        <span class="scene-switch"></span>
                    </span>
                    <span class="scene-filter is-switch">
                        <span class="scene-filter-text">
                            <svg><use href="#symbols-eye-closed"></use></svg>
                            <span>Include Hidden</span>
                        </span>
                        <span class="scene-switch"></span>
                    </span>
                </div>
                <span class="scene-default-buttons">
                    <span>Save as default</span>
                    <span>Reset to default</span>
                </span>
            </div>
            <span class="scene-shuffle-button">
                <svg><use href="#symbols-shuffle"></use></svg>
                Shuffle
            </span>
        </div>
    `
}

function browseSceneTemplate(): string {
	const rows = [
		{
			cover: 8,
			title: 'Blue Valentine',
			artist: 'Tom Waits',
			details: '1978, Vinyl',
			lastHeard: 'Sep 2, 2026',
			count: 9,
		},
		{
			cover: 7,
			title: 'Lethal League OST',
			artist: 'Various',
			details: '2018, Vinyl, LP',
			lastHeard: 'Oct 20, 2026',
			count: 5,
		},
		// Continues under the screen's bottom fade.
		{
			cover: 5,
			title: 'Night Drive',
			artist: 'The Lanterns',
			details: '1978, Vinyl',
			lastHeard: 'Aug 14, 2026',
			count: 3,
		},
	]

	return `
        <div class="scene-screen scene-browse">
            <div class="scene-page-head">
                <strong>Collection</strong>
                <span class="scene-badge">120</span>
            </div>
            <div class="scene-search-row">
                <span class="scene-search">${SEARCH_ICON}Search collection</span>
                <span class="scene-icon-box">${FUNNEL_ICON}</span>
            </div>
            <ul class="scene-list">
                ${rows
					.map(
						(row) => `
                            <li class="scene-row">
                                ${coverTemplate(row.cover)}
                                <span class="scene-row-text">
                                    <strong>${row.title}</strong>
                                    <span>${row.artist}</span>
                                    <span>${row.details}</span>
                                    <span><em>Last heard:</em> ${row.lastHeard}, <em>Count:</em> ${row.count}</span>
                                </span>
                            </li>
                        `,
					)
					.join('')}
            </ul>
        </div>
    `
}

function historySceneTemplate(): string {
	return `
        <div class="scene-screen scene-release">
            <strong class="scene-release-title">Lethal League OST</strong>
            <span class="scene-release-artist">Various</span>
            <span class="scene-release-year">2018</span>
            ${coverTemplate(7)}
            <div class="scene-release-actions">
                <span class="scene-round-button"><svg><use href="#symbols-heart"></use></svg></span>
                <span class="scene-log-button">
                    <svg><use href="#symbols-check-circle"></use></svg>
                    Log play
                </span>
                <span class="scene-round-button"><svg><use href="#symbols-pencil"></use></svg></span>
            </div>
            <dl class="scene-info-table">
                <dt>Last heard:</dt>
                <dd>${swapTemplate('<em>No Record</em>', 'Today')}</dd>
                <dt>Count:</dt>
                <dd>${swapTemplate('<em>No Record</em>', '1')}</dd>
                <dt>Released:</dt>
                <dd>Jun 2022 (US)</dd>
                <dt>Format:</dt>
                <dd>Vinyl, LP</dd>
            </dl>
        </div>
    `
}

function freshSceneTemplate(): string {
	return `
        <div class="scene-frame">
            <div class="scene-screen scene-dialog-screen"></div>
            <div class="scene-dialog">
                <strong class="scene-heading">Log a play</strong>
                <span class="scene-field-label">Listened on:</span>
                <span class="scene-field">10/31/2026</span>
                <span class="scene-switch-row">
                    Exact date unknown
                    <span class="scene-switch"></span>
                </span>
                <span class="scene-field-label">Hide from shuffle for:</span>
                <span class="scene-field is-select">
                    ${swapTemplate("Don't hide", '3 months')}
                    <svg><use href="#symbols-arrow-down"></use></svg>
                </span>
                <span class="scene-dialog-actions">
                    <span class="scene-dialog-button">Cancel</span>
                    <span class="scene-dialog-button is-primary">Save</span>
                </span>
            </div>
        </div>
    `
}

function friendsSceneTemplate(): string {
	const events = [
		{
			initials: 'MA',
			name: 'mara',
			action: 'listened to',
			extra: '',
			time: '1 hour ago',
			cover: 8,
			title: 'Blue Valentine',
			subtitle: 'Tom Waits · 1978',
		},
		{
			initials: 'JO',
			name: 'jonas',
			action: 'rated',
			extra: starsTemplate(4),
			time: '2 hours ago',
			cover: 6,
			title: 'Slow Orbit',
			subtitle: 'The Meridians · 1985',
		},
		{
			initials: 'SA',
			name: 'sam',
			action: 'liked',
			extra: '',
			time: 'yesterday',
			cover: 7,
			title: 'Lethal League OST',
			subtitle: 'Various · 2018',
		},
	]

	return `
        <div class="scene-screen scene-activity">
            <strong class="scene-heading is-start">Activity</strong>
            <ul class="scene-feed">
                ${events
					.map(
						(event) => `
                            <li class="scene-event">
                                <span class="scene-avatar">${event.initials}</span>
                                <span class="scene-event-body">
                                    <span class="scene-event-head">
                                        <span><strong>${event.name}</strong> ${event.action} ${event.extra}</span>
                                        <time>${event.time}</time>
                                    </span>
                                    <span class="scene-event-release">
                                        ${coverTemplate(event.cover)}
                                        <span>
                                            <strong>${event.title}</strong>
                                            <span>${event.subtitle}</span>
                                        </span>
                                    </span>
                                </span>
                            </li>
                        `,
					)
					.join('')}
            </ul>
        </div>
    `
}
